import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, User, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import api from "../lib/api";

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const searchChildren = async () => {
      if (query.length < 2) {
        setResults([]);
        return;
      }

      setLoading(true);
      try {
        const response = await api.get("/search/children", {
          params: { q: query, limit: 10 }
        });
        setResults(response.data);
        setIsOpen(true);
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchChildren, 300);
    return () => clearTimeout(debounce);
  }, [query]);

  const handleSelect = (childId) => {
    navigate(`/children/${childId}`);
    setQuery("");
    setIsOpen(false);
    setResults([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div ref={searchRef} className="relative w-full max-w-md" data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search children..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          className="pl-10 pr-10 bg-slate-50 border-slate-200 focus:bg-white"
          data-testid="global-search-input"
        />
        {query && (
          <button
            onClick={() => {
              setQuery("");
              setResults([]);
              setIsOpen(false);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        )}
      </div>

      {/* Results dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-slate-100 overflow-hidden z-50 max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-slate-500">Searching...</div>
          ) : results.length > 0 ? (
            <div className="py-2">
              {results.map((child) => (
                <button
                  key={child.id}
                  onClick={() => handleSelect(child.id)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors text-left"
                  data-testid={`search-result-${child.id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sage-400 to-sage-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {child.first_name[0]}{child.last_name[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800">
                      {child.first_name} {child.last_name}
                    </p>
                    {child.enrollment && (
                      <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                        {child.enrollment.center_name && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {child.enrollment.center_name}
                          </span>
                        )}
                        {child.enrollment.room_name && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {child.enrollment.room_name}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : query.length >= 2 ? (
            <div className="p-4 text-center text-slate-500">No children found</div>
          ) : null}
        </div>
      )}
    </div>
  );
}
